/**
 * `useTeamUsers` — the tenant team-members list (`GET /api/t/{slug}/users`).
 *
 * Extracted as a shared query hook so the Settings hub's Team preview card can
 * read the SAME cache the Team page populates (key = `teamUsersKey(slug)` =
 * `['team', slug, 'users']`). The Team page declares an in-file copy with the
 * identical key + shape; this hook is the reusable surface for read-only
 * consumers (the hub preview) that don't pull in the Team page's mutations.
 */
import { useQuery } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { apiClient } from "@/lib/apiClient";
import { ApiError } from "@/lib/apiError";
import { teamUsersKey } from "@/hooks/queries/users/teamUserKeys";

/** One team member as returned by the tenant users-list endpoint. */
export interface TeamUserSummary {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  role: string;
  /** Active when null/absent; a timestamp string means deactivated. */
  deactivated_at?: string | null;
}

export function useTeamUsers() {
  const { tenantSlug } = useParams<{ tenantSlug: string }>();

  return useQuery<TeamUserSummary[], ApiError>({
    queryKey: tenantSlug ? teamUsersKey(tenantSlug) : ["team", "_pending"],
    queryFn: async () => {
      const r = await apiClient.get<TeamUserSummary[]>(
        `/api/t/${encodeURIComponent(tenantSlug!)}/users`,
      );
      return r.data;
    },
    enabled: !!tenantSlug,
  });
}
